export const trimObject = (object: any, removals = [undefined, null, ""]) => {
  if (typeof object !== "object" || Array.isArray(object)) {
    return object;
  }

  const result: any = {};
  Object.entries(object).forEach(([key, value]: [string, any]) => {
    if (!removals.includes(value)) {
      result[key] = value;
    }
  });
  return result;
};

export function getUniqueItems<T extends Record<string, any>, K extends keyof T>(
  array: T[],
  property: K
): Map<string, NonNullable<T[K]>> {
  const uniqueMap = new Map<string, NonNullable<T[K]>>();

  for (const item of array) {
    const propValue = item[property];
    if (propValue && typeof propValue === "object" && "id" in propValue) {
      uniqueMap.set(String(propValue.id), propValue as NonNullable<T[K]>);
    }
  }

  return uniqueMap;
}

export function toggleArrayValue<T>(current: T[] | null | undefined, value: T): T[] {
  const array = current ? [...current] : [];
  const index = array.indexOf(value);
  if (index >= 0) {
    array.splice(index, 1);
  } else {
    array.push(value);
  }
  return array;
}

/**
 * Safe deep object access, similar to lodash.get
 *
 * @param obj - The object to query
 * @param path - The path to the property (string or array)
 * @param defaultValue - The value returned if the resolved value is undefined
 *
 * const obj = {
 *   user: {
 *     profile: {
 *       name: 'Alice',
 *       roles: ['admin', 'editor']
 *     }
 *   }
 * };
 *
 * get(obj, 'user.profile.name');               // 'Alice'
 * get(obj, ['user', 'profile', 'roles', 0]);   // 'admin'
 * get(obj, 'user.profile.age', 30);            // 30 (default)
 * get(obj, 'user.profile.roles[1]');           // 'editor'
 */
export function get<T = any, R = any>(obj: T, path: string | Array<string | number>, defaultValue?: R): R | any {
  if (!obj || typeof obj !== "object") {
    return defaultValue;
  }

  const pathArray = Array.isArray(path)
    ? path
    : path
        .replace(/\[(\w+)\]/g, ".$1") // convert [0] to .0
        .replace(/^\./, "") // remove leading dot
        .split(".");

  let result: any = obj;
  for (const key of pathArray) {
    if (result && typeof result === "object" && key in result) {
      result = result[key];
    } else {
      return defaultValue;
    }
  }

  return result === undefined ? defaultValue : result;
}

/**
 * Generic function to sort an array of objects by a single property
 *
 * @param array - Array of objects to sort
 * @param property - Property name to sort by
 * @param direction - Sort direction ('asc' or 'desc')
 * @returns Sorted array
 */
export function sortBy<T, K extends keyof T>(
  array: T[],
  property: K,
  direction: 'asc' | 'desc' = 'asc'
): T[] {
  return array.sort((a, b) => {
    const valueA = a[property];
    const valueB = b[property];

    // Handle null/undefined values
    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return direction === 'asc' ? 1 : -1;
    if (valueB == null) return direction === 'asc' ? -1 : 1;

    let comparison = 0;

    // Handle different types
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      comparison = valueA.localeCompare(valueB);
    } else if (typeof valueA === 'number' && typeof valueB === 'number') {
      comparison = valueA - valueB;
    } else if (valueA instanceof Date && valueB instanceof Date) {
      comparison = valueA.getTime() - valueB.getTime();
    } else {
      // Fallback to string comparison
      comparison = String(valueA).localeCompare(String(valueB));
    }

    return direction === 'asc' ? comparison : -comparison;
  });
}
